import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import AuthShell from "@/pages/auth/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, formatApiError, roleHome } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

export default function Login() {
  const { login } = useAuth();
  const { lang } = useI18n();
  const L = (it, en, de) => (lang === "en" ? en : lang === "de" ? (de ?? it) : it);
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(L("Accesso effettuato", "Signed in", "Angemeldet"));
      if (user.must_change_password) { navigate("/cambia-password", { replace: true }); return; }
      const from = location.state?.from;
      navigate(from && !from.startsWith("/login") ? from : roleHome(user.role), { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title={L("Accedi", "Sign in", "Anmelden")}
      subtitle={L("Inserisci le tue credenziali per accedere all'area riservata.", "Enter your credentials to access the reserved area.", "Geben Sie Ihre Zugangsdaten ein, um den geschützten Bereich zu betreten.")}
      footer={<>{L("Non hai un account?", "Don't have an account?", "Noch kein Konto?")} <Link to="/registrazione" className="text-cyanx hover:text-white">{L("Registrati", "Register", "Registrieren")}</Link></>}
    >
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <Label className="text-slate-300">{L("Email", "Email", "E-Mail")}</Label>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className="mt-1.5 bg-navy-paper border-white/10 h-11" placeholder="nome@esempio.com" />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-slate-300">{L("Password", "Password", "Passwort")}</Label>
            <Link to="/password-dimenticata" className="text-xs text-cyanx hover:text-white">{L("Password dimenticata?", "Forgot password?", "Passwort vergessen?")}</Link>
          </div>
          <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="mt-1.5 bg-navy-paper border-white/10 h-11" placeholder="••••••••" />
        </div>
        {error && <p className="text-sm text-red-400" data-testid="login-error">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full bg-gold text-navy hover:bg-gold-hover h-11 font-medium" data-testid="login-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : L("Accedi", "Sign in", "Anmelden")}
        </Button>
      </form>
    </AuthShell>
  );
}
